import { useCallback } from "react";
import { t } from "i18next";
import { useRootStore } from "../../../stores/hooks";
import type { BranchRef } from "../../../stores/automation/branches.store";

/**
 * Shared delete-branch flow (spec: Deletion): an empty branch deletes
 * immediately; a branch with content asks for confirmation first. Consumed
 * by the block settings panel (BranchOption trash) and the canvas branch
 * card kebab. onDeleted fires only after an actual deletion -- callers use
 * it to re-point the selection at the block panel.
 */
export const useDeleteBranch = (
  instanceId: number,
  onDeleted?: (branchHash: string) => void,
): ((branchHash: string | "else") => void) => {
  const store = useRootStore();

  return useCallback(
    (branchHash: string | "else"): void => {
      const branches = store.automations.candidate.branches;
      const ref: BranchRef = { instanceId, branchHash };

      const performDelete = (): void => {
        if (branches.deleteBranch(ref)) {
          onDeleted?.(String(branchHash));
        }
      };

      if (!branches.branchHasContent(ref)) {
        performDelete();
        return;
      }

      store.modal.openConfirmModal({
        type: "warning",
        title: t("branches.delete-confirm-title"),
        description: t("branches.delete-branch-confirm"),
        positiveButtonText: t("branches.delete-confirm-confirm"),
        negativeButtonText: t("branches.delete-confirm-cancel"),
        onClose: () => {
          store.modal.closeConfirmModal();
        },
        onConfirm: () => {
          performDelete();
          store.modal.closeConfirmModal();
        },
      });
    },
    [instanceId, onDeleted, store],
  );
};
